import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { colors as dsColors, mediaQuery } from '@modernhealth/vital-foundation';
import { useMediaQuery } from '@react-hook/media-query';
import styled from '@emotion/styled';
import {
    ComponentTextSmall,
    ComponentTextXSmall,
    HeadingText30,
    HeadingText40,
    Button,
    pxToRem,
} from '@modernhealth/vital-components';
import { useAnalytics } from '@modernhealth/web-analytics';

import { navigationService } from 'src/config/rootService';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';

import { BenefitBannerTestID } from '../models/testsIds';

interface BenefitsBannerCardProps {
    companyName: string;
}

export const BenefitBannerCard: React.FC<BenefitsBannerCardProps> = ({ companyName }) => {
    const { t } = useTranslation();
    const { companySegments, companySegment } = useSelector(getUserInformation);
    const analytics = useAnalytics();
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    const TitleTextComponent = smallScreenDetected ? HeadingText30 : HeadingText40;
    const SubtitleTextComponent = smallScreenDetected ? ComponentTextXSmall : ComponentTextSmall;
    const handleBenefitsBanner = () => {
        if (!!companySegment || companySegments?.length === 0) {
            navigationService.navigate('/all-your-benefits');
            analytics.trackClick('homepage: explore your benefits', {
                home_section: 'explore_your_benefits',
            });
        } else {
            navigationService.navigate('/choose-your-segment');
            analytics.trackClick('homepage: explore your benefits', {
                home_section: 'choose_your_segment',
            });
        }
    };
    return (
        <Container data-testid={BenefitBannerTestID.Container}>
            <ImageContainer>
                <img
                    src="https://assets.joinmodernhealth.com/benefits/benefits-banner-web.svg"
                    className="image"
                    alt=""
                />
            </ImageContainer>

            <TextContainer>
                <TitleTextComponent fontColor="black">
                    {t('dashboard:explore_benefits.title')}
                </TitleTextComponent>
                <SubtitleTextComponent fontColor="gray65" marginBottom={8}>
                    {t('dashboard:explore_benefits.sub_title', { company: companyName })}
                </SubtitleTextComponent>
            </TextContainer>
            <ButtonContainer>
                <Button
                    size="medium"
                    variant="primary"
                    data-testid={BenefitBannerTestID.Button}
                    rounded
                    onClick={handleBenefitsBanner}
                >
                    {t('dashboard:explore_benefits.button')}
                </Button>
            </ButtonContainer>
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    gap: pxToRem(28),
    minHeight: pxToRem(120),
    marginTop: pxToRem(20),
    marginBottom: pxToRem(48),
    position: 'relative',
    width: '100%',

    //border
    borderStyle: 'solid',
    borderWidth: pxToRem(1),
    borderRadius: pxToRem(8),
    borderColor: dsColors.gray05,
});

const TextContainer = styled.div({
    flexBasis: '75%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: pxToRem(4),
});

const ImageContainer = styled.div({
    bottom: 0,
    left: 0,

    marginLeft: pxToRem(24),
    maxWidth: pxToRem(108),
    maxHeight: '100%',
    overflow: 'hidden',
    flexBasis: '25%',
    width: '40%',

    [mediaQuery.maxWidth('md')]: {
        maxWidth: pxToRem(50),
    },

    '.image': {
        width: '90%',
        marginTop: pxToRem(8),
    },
});

const ButtonContainer = styled.div({
    width: '18%',
});
